import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { PredefinedChipOptions } from "./customer-widget/customer-widget";
import { QuickActionZod } from "./team";

// noop forces the intellisense to resolve, instead of listing everything in the giant pre-pick type
// See https://stackoverflow.com/questions/69976130/can-i-force-typescript-to-resolve-the-type-of-pickt-k-for-display
export type NOOP<T> = T extends (...args: any[]) => any
  ? T
  : T extends abstract new (...args: any[]) => any
    ? T
    : { [K in keyof T]: T[K] };

/**
 * Flat Zod schema for team settings based on the flattened structure.
 * This schema represents settings paths like "support/ai/enabled" and "concierge/general/name"
 * as flat keys, eliminating the need for complex nested object updates.
 */

// Note: Removed non-customer experience schemas (loyalty, marketing, returns, etc.)
// Keeping only customer experience and AI related settings. Other ones can be ported over as needed.

// Base types and enums
const ResponseLength = z.string();
const TicketHandoffType = createEnum(["EMAIL", "LIVE_CHAT"]);

const AccountTabSectionType = createEnum([
  "wish_list",
  "recent_orders",
  "loyalty_points",
  "viewed_products",
  "product_recommendations",
]);
const PillTheme = z.string();
const SenderEmailSetupStatus = createEnum(["connected", "unverified"]);

const SenderEmailSchema = z.object({
  email: z.email(),
  emailSignature: z.string().nullish(),
  name: z.string(),
  postmarkId: z.coerce.number().int(),
  domainId: z.coerce.number().int(),
  dkimHost: z.string(),
  dkimText: z.string(),
  returnPathDomain: z.string(),
  returnPathValue: z.string(),
  setupStatus: SenderEmailSetupStatus,
});

// Utility schemas
const ISODateString = z.iso
  .datetime()
  .transform((data) => new Date(data))
  .or(z.date());
const TimeString = z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/); // HH:MM format

/**
 * Creates a zod schema with prefixed keys for flat structure.
 * E.g., prefix "support/ai" with { enabled: z.boolean() } becomes { "support/ai/enabled": z.boolean() }
 */
function createPrefixedSchema<
  T extends Record<string, z.ZodTypeAny>,
  P extends string,
>(prefix: P, schema: T) {
  const prefixedShape = {} as { [K in keyof T as `${P}/${string & K}`]: T[K] };
  for (const [key, zodType] of Object.entries(schema)) {
    (prefixedShape as any)[`${prefix}/${key}`] = zodType;
  }
  return z.object(prefixedShape);
}

const DailyScheduleSchema = z
  .object({
    enabled: z.boolean().nullish(),
    start: TimeString.nullish(),
    end: TimeString.nullish(),
  })
  .nullish();

const SupportSettingsSchema_ = z.object({
  "support/enabled": z.boolean().nullish(),
  "support/defaultEmail": z.email().nullish(),
  "support/defaultSendAction": z.string().nullish(),
  "support/emailSignature": z.string().nullish(),
  "support/aiEmailSignature": z.string().nullish(),
  "support/excludedEmails": z.array(z.string().nullable()).nullish(),
  "support/senderEmails": z.array(SenderEmailSchema).nullish(),
  "support/maxTicketsPerUser": z.coerce.number().nullish(),
  "support/ticketAssignment": z.string().nullish(),
  "support/usersForAutoAssignment": z.array(zExt.objectId()).nullish(),
  "support/autoAssignOnResponse": z.boolean().nullish(),
  "support/autoReassignOnResponse": z.boolean().nullish(),
  "support/keepAssigneeOnTicketReopen": z.boolean().nullish(),
  "support/useInProgressStatus": z.boolean().nullish(),
  "support/closeAction": z.string().nullish(),
  "support/undoSend": z.boolean().nullish(),
  "support/autoMergeEnabled": z.boolean().nullish(),
  "support/propagateDeletes": z.boolean().nullish(),
  "support/closeTicketsOnGmailArchive": z.boolean().nullish(),

  // AI Settings
  ...createPrefixedSchema("support/ai", {
    enabled: z.boolean().nullish(),
    respondToInternalEmails: z.boolean().nullish(),
    copilotEnabled: z.boolean().nullish(),
    autoGenerateResponses: z.boolean().nullish(),
    useEmailSignature: z.boolean().nullish(),
    autopilotEnabled: z.boolean().nullish(),
    automateLiveChat: z.boolean().nullish(),
    autoCloseNoReplyTickets: z.boolean().nullish(),
    aiAutopilotTicketVolume: z.number().nullish(),
    trialMessagesRemaining: z.number().int().min(0).nullish(),
    hasTriedAi: z.boolean().nullish(),
    whitelistedDomains: z.array(z.string()).nullish(),
    botGreeting: z.string().nullish(),
    humanHandoffMessage: z.string().nullish(),
    emailVerificationRequired: z.boolean().nullish(),
    ticketDescription: z.string().nullish(),
    disallowedPrompts: z.array(z.string()).nullish(),
    disallowedPromptsEnabled: z.boolean().nullish(),
    generateSuggestedKnowledge: z.boolean().nullish(),
    "autopilotToolSettings/cancelOrder/cancelOrderTimeWindowAfterPlacingMinutes":
      z.number().int().min(0).nullish(),
    "autopilotToolSettings/updateOrderAddress/updateOrderAddressTimeWindowAfterPlacingMinutes":
      z.number().int().min(0).nullish(),
  }).shape,

  // AI Enabled Platforms
  ...createPrefixedSchema("support/ai/enabledPlatforms", {
    email: z.boolean().nullish(),
    facebook: z.boolean().nullish(),
    instagram: z.boolean().nullish(),
    sms: z.boolean().nullish(),
    tiktok: z.boolean().nullish(),
    attentive: z.boolean().nullish(),
    postscript: z.boolean().nullish(),
  }).shape,

  // Support Billing - Complex nested structure with ObjectIds and dates
  ...createPrefixedSchema("support/billing/base", {
    pricePerMonthInCents: z.number().int().min(0).nullish(),
    subscriptionActive: z.boolean().nullish(),
    createdAt: ISODateString.nullish(),
    updatedAt: ISODateString.nullish(),
    billingStartDate: ISODateString.nullish(),
    freeTrialEndDate: ISODateString.nullish(),
    version: z.string().nullish(),
  }).shape,

  ...createPrefixedSchema("support/billing/ai", {
    aiResolutionPriceInCents: z.number().min(0).nullish(),
    atShopifyCap: z.boolean().nullish(),
    billingAccepted: z.boolean().nullish(),
    billingStartDate: ISODateString.nullish(),
    freeTrialDays: z.number().int().min(0).nullish(),
    freeTrialEndDate: ISODateString.nullish(),
    freeTrialUsed: z.boolean().nullish(),
    ticketsUsed: z.number().int().min(0).nullish(),
    createdAt: ISODateString.nullish(),
    updatedAt: ISODateString.nullish(),
    pricePerMonth: z.number().min(0).nullish(),
    ticketsCovered: z.number().int().min(0).nullish(),
  }).shape,

  // Voice Settings with complex nested structure
  ...createPrefixedSchema("support/voice", {
    enabled: z.boolean().nullish(),
    availableHoursEnabled: z.boolean().nullish(),
    availableHoursTimezone: z.string().nullish(),
    ringingStrategy: createEnum(["ring-team", "ring-all"]).nullish(),
    allowHoldingCallsInQueue: z.boolean().nullish(),
    sendToVoicemailWhenAllAgentsAreOffline: z.boolean().nullish(),
    playAutomatedMessageWhenAllAgentsAreOffline: z.boolean().nullish(),
    holdMusicName: z.string().nullish(),
    holdMusicUrl: z.string().nullish(),
    billingGroupId: z.string().nullish(),
    sipConnectionId: z.string().nullish(),
    sipConnectionCredential: z.string().nullish(),
    voiceApplicationId: z.string().nullish(),
    outboundVoiceProfileId: z.string().nullish(),
  }).shape,

  // Voice Available Hours
  ...createPrefixedSchema("support/voice/availableHours", {
    monday: DailyScheduleSchema,
    tuesday: DailyScheduleSchema,
    wednesday: DailyScheduleSchema,
    thursday: DailyScheduleSchema,
    friday: DailyScheduleSchema,
    saturday: DailyScheduleSchema,
    sunday: DailyScheduleSchema,
  }).shape,

  // Voice Messages with ObjectIds
  ...createPrefixedSchema("support/voice/inboundGreetingMessage", {
    enabled: z.boolean().nullish(),
    type: z.string().nullish(),
    ttsMessage: z.string().nullish(),
    audioName: z.string().nullish(),
    audioUrl: z.string().nullish(),
  }).shape,
  ...createPrefixedSchema("support/voice/outboundGreetingMessage", {
    enabled: z.boolean().nullish(),
    type: z.string().nullish(),
    ttsMessage: z.string().nullish(),
    audioName: z.string().nullish(),
    audioUrl: z.string().nullish(),
  }).shape,
  ...createPrefixedSchema("support/voice/offHoursMessage", {
    enabled: z.boolean().nullish(),
    type: z.string().nullish(),
    ttsMessage: z.string().nullish(),
    audioName: z.string().nullish(),
    audioUrl: z.string().nullish(),
  }).shape,
  ...createPrefixedSchema("support/voice/voicemailMessage", {
    enabled: z.boolean().nullish(),
    type: z.string().nullish(),
    ttsMessage: z.string().nullish(),
    audioName: z.string().nullish(),
    audioUrl: z.string().nullish(),
  }).shape,

  "support/tags": z
    .array(z.object({ name: z.string(), pillTheme: PillTheme }))
    .nullish(),

  ...createPrefixedSchema("support/billing/textMessaging", {
    enabled: z.boolean().nullish(),
    billingFailed: z.boolean().nullish(),
    subscriptionActive: z.boolean().nullish(),
    monthlyMinimumInTenthCents: z.number().int().min(0).nullish(),
    pricePerSMSInTenthCents: z.number().int().min(0).nullish(),
    pricePerMMSInTenthCents: z.number().int().min(0).nullish(),
    previousCycles: z.array(z.any()).nullish(),
  }).shape,
  ...createPrefixedSchema("support/billing/textMessaging/currentBillingCycle", {
    billingCycleStartDate: ISODateString.nullish(),
    lastBilledAt: ISODateString.nullish(),
    monthlyMinimumRemainingInTenthCents: z.number().int().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema(
    "support/billing/textMessaging/currentBillingCycle/billed",
    {
      smsMessagesReceived: z.number().int().min(0).nullish(),
      smsMessagesSent: z.number().int().min(0).nullish(),
      mmsMessagesReceived: z.number().int().min(0).nullish(),
      mmsMessagesSent: z.number().int().min(0).nullish(),
    },
  ).shape,
  ...createPrefixedSchema("support/billing/textMessaging/unbilled", {
    smsMessagesReceived: z.number().int().min(0).nullish(),
    smsMessagesSent: z.number().int().min(0).nullish(),
    mmsMessagesReceived: z.number().int().min(0).nullish(),
    mmsMessagesSent: z.number().int().min(0).nullish(),
  }).shape,

  ...createPrefixedSchema("support/billing/voice", {
    billingStartDate: ISODateString.nullish(),
    createdAt: ISODateString.nullish(),
    updatedAt: ISODateString.nullish(),
    pricePerMinuteInCents: z.number().int().min(0).nullish(),
    pricePerMonthInCents: z.number().int().min(0).nullish(),
    subscriptionActive: z.boolean().nullish(),
  }).shape,

  ...createPrefixedSchema("support/contactForm", {
    enabled: z.boolean().nullish(),
    title: z.string().nullish(),
    successMessage: z.string().nullish(),
    customCss: z.string().nullish(),
    allowFileUpload: z.boolean().nullish(),
    customFields: z.array(z.any()).nullish(),
  }).shape,
  ...createPrefixedSchema("support/contactForm/name", {
    placeholder: z.string().nullish(),
    required: z.boolean().nullish(),
    show: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("support/contactForm/email", {
    placeholder: z.string().nullish(),
  }).shape,
  ...createPrefixedSchema("support/contactForm/phone", {
    placeholder: z.string().nullish(),
    required: z.boolean().nullish(),
    show: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("support/contactForm/subject", {
    placeholder: z.string().nullish(),
    required: z.boolean().nullish(),
    show: z.boolean().nullish(),
    options: z.array(z.string()).nullish(),
  }).shape,
  ...createPrefixedSchema("support/contactForm/comment", {
    placeholder: z.string().nullish(),
  }).shape,
  ...createPrefixedSchema("support/contactForm/replyEmail", {
    email: z.email().nullish(),
    name: z.string().nullish(),
    integrationKind: z.string().nullish(),
  }).shape,

  ...createPrefixedSchema("support/csat", {
    enabled: z.boolean().nullish(),
    allowRemarks: z.boolean().nullish(),
    surveyMessage: z.string().nullish(),
    surveySendTime: z.string().nullish(),
    surveyMethods: z.array(z.string()).nullish(),
    headerImage: z.string().nullish(),
  }).shape,

  ...createPrefixedSchema("support/facebook", {
    autoCloseExternal: z.boolean().nullish(),
    commentsEnabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("support/instagram", {
    autoCloseExternal: z.boolean().nullish(),
    commentsEnabled: z.boolean().nullish(),
    autoCloseMentions: z.boolean().nullish(),
    autoCloseRepliedStories: z.boolean().nullish(),
  }).shape,

  ...createPrefixedSchema("support/sms", {
    enabled: z.boolean().nullish(),
    privacyPolicyUrl: z.string().nullish(),
    termsAndConditionsUrl: z.string().nullish(),
  }).shape,

  "support/metaPagesData/pagesData": z.array(z.any()).nullish(),
  "support/faq/brandKitOverrides/pageBackgroundColor": z.boolean().nullish(),
  ...createPrefixedSchema("support/faq", {
    collectionBackgroundColor: z.string().nullish(),
    collectionBorderColor: z.string().nullish(),
    collectionCornerRadius: z.number().min(0).nullish(),
    displayCollectionIcon: z.boolean().nullish(),
    displayCollectionSubtext: z.boolean().nullish(),
    headlineText: z.string().nullish(),
    headlineTextColor: z.string().nullish(),
    homeBackgroundImageUrl: z.string().nullish(),
    overlayColor: z.string().nullish(),
    overlayOpacity: z.number().nullish(),
    pageBackgroundColor: z.string().nullish(),
  }).shape,
});

const ConciergeSettingsSchema_ = z.object({
  ...createPrefixedSchema("concierge/general", {
    name: z.string().nullish(),
    toneOfVoice: z.string().nullish(),
    brandDescription: z.string().nullish(),
    customerDescription: z.string().nullish(),
    outputLength: ResponseLength.nullish(),
    welcomeMessage: z.string().nullish(),
    talkingPointsKnowledgeBase: z.string().nullish(),
    enableAiWelcomeMessage: z.boolean().nullish(),
    enableTalkingPointsKb: z.boolean().nullish(),
    suggestedResponsesEnabled: z.boolean().nullish(),
    writingExamples: z.array(z.any()).nullish(),
  }).shape,
  "concierge/enabled": z.boolean().nullish(),
  "concierge/excludedProductTags": z.array(z.string()).nullish(),
  "concierge/overriddenBestSellers": z.array(z.string()).nullish(),
  "concierge/context/productMetafieldKeys": z.array(z.string()).nullish(),
  "concierge/placeProductFormFromThemeExtension": z.boolean().nullish(),
  "concierge/themeExtensionProductFormPlaceholder": z.string().nullish(),
  "concierge/themeExtensionProductFormButtonText": z.string().nullish(),
  "concierge/themeExtensionProductFormFontSize": z.string().nullish(),
  "concierge/themeExtensionProductFormBorderRadius": z.string().nullish(),
  "concierge/themeExtensionProductFormButtonBackgroundColor": z
    .string()
    .nullish(),
  "concierge/themeExtensionProductFormButtonTextColor": z.string().nullish(),
  "concierge/themeExtensionProductFormCustomCss": z.string().nullish(),
  "concierge/proactiveChipEngagement": z.boolean().nullish(),
  "concierge/questionPromptCount": z.number().int().min(0).nullish(),
  ...createPrefixedSchema("concierge/skills/backInStockNotifications", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/cancelOrder", {
    enabled: z.boolean().nullish(),
    cancelOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/editAddress", {
    enabled: z.boolean().nullish(),
    updateOrderAddressTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/editOrderQuantity", {
    enabled: z.boolean().nullish(),
    editOrderQuantityTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/orderTracking", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/cartEditing", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/productBundle", {
    enabled: z.boolean().nullish(),
    maxDiscountPercentage: z.number().min(0).max(10).nullish(),
    discountCodeExpirationMinutes: z.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/createDiscountCode", {
    enabled: z.boolean().nullish(),
    maxDiscountPercentage: z.number().min(0).nullish(),
    discountCodeExpirationMinutes: z.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/updateOrder", {
    enabled: z.boolean().nullish(),
    updateOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/webScrapingForProductInformation", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/forceUseRetrieveSupportKnowledge", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/productVisualization", {
    enabled: z.boolean().nullish(),
  }).shape,
  "concierge/skills/returnsPortalLinkOverride/url": z.string().nullish(),
  ...createPrefixedSchema("concierge/emailSignUp", {
    buttonLabel: z.string().nullish(),
    incentivize: z.boolean().nullish(),
    signUpDiscountAmount: z.coerce.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/narrowProductRecommendation", {
    enabled: z.boolean().nullish(),
    productCount: z.number().int().min(1).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/guardrails", {
    storeContactInformation: z.string().nullish(),
  }).shape,
});

const ConciergeTicketHandoffSettingsSchema_ = createPrefixedSchema(
  "concierge/ticketHandoffSettings",
  {
    handoffType: TicketHandoffType.nullish(),
    emailHandoffMessage: z.string().nullish(),
    handoffEmail: z.email().nullish(),
    ccCustomer: z.boolean().nullish(),
    customerCcMessage: z.string().nullish(),
    includeTranscriptInEmail: z.boolean().nullish(),
    enableTicketCreation: z.boolean().nullish(),
    collectCustomerEmailBeforeHandoff: z.boolean().nullish(),
    forwardToExternalEmail: z.boolean().nullish(),
  },
);

const CustomerWidgetSettingsSchema_ = z.object({
  ...createPrefixedSchema("customerWidget/general", {
    widgetColor: z.string().nullish(),
    widgetSecondaryColor: z.string().nullish(),
    widgetSecondaryColorEnabled: z.boolean().nullish(),
    hideButton: z.boolean().nullish(),
    widgetPosition: createEnum([
      "bottom-right",
      "bottom-left",
      "left",
      "right",
    ]).nullish(),
    widgetButtonFontSize: z.coerce.number().int().min(6).max(24).nullish(),
    widgetHeaderFontSize: z.coerce.number().int().min(6).max(72).nullish(),
    widgetMessageBubbleFontSize: z.coerce
      .number()
      .int()
      .min(6)
      .max(24)
      .nullish(),
    widgetQuickActionFontSize: z.coerce.number().int().min(6).max(24).nullish(),
    widgetQuickLinkFontSize: z.coerce.number().int().min(6).max(24).nullish(),
    widgetFontFamily: z.string().nullish(),
    headingLogoUrl: z.url().nullish(),
    mainHeadingText: z.string().nullish(),
    homeHeadingText: z.string().nullish(),
    bubbleButtonText: z.string().nullish(),
    chatWithUsButtonText: z.string().nullish(),
    customCss: z.string().nullish(),
    enableInAppEmbed: z.boolean().nullish(),
    openToWelcomePage: z.boolean().nullish(),
    mobileFullscreen: z.boolean().nullish(),
    showCustomerWidgetInReturnApp: z.boolean().nullish(),
    xAdjustment: z.coerce.number().nullish(),
    yAdjustment: z.coerce.number().nullish(),
    mobileXAdjustment: z.coerce.number().nullish(),
    mobileYAdjustment: z.coerce.number().nullish(),
    hideWidgetAfterHours: z.boolean().nullish(),
    hideWidgetOnProductPages: z.boolean().nullish(),
    widgetHoursEnabled: z.boolean().nullish(),
    widgetHoursTimezone: z.string().nullish(),
    customerWidgetV2Enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/gradientSetting", {
    gradientStrength: z.number().min(0).max(1).nullish(),
    inverted: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/chips", {
    customChips: z.array(z.string()).nullish(),
    selectedPredefinedChips: z
      .array(z.nativeEnum(PredefinedChipOptions))
      .nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/selfServe", {
    cancelOrderEnabled: z.boolean().nullish(),
    cancelOrderLabel: z.string().nullish(),
    startClaimEnabled: z.boolean().nullish(),
    startReturnEnabled: z.boolean().nullish(),
    startReturnLabel: z.string().nullish(),
    trackPackageEnabled: z.boolean().nullish(),
    trackPackageSettingLabel: z.string().nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/widgetHours/monday", {
    enabled: z.boolean().nullish(),
    timeBlocks: z
      .array(
        z.object({ start: TimeString.nullish(), end: TimeString.nullish() }),
      )
      .nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/widgetHours/tuesday", {
    enabled: z.boolean().nullish(),
    timeBlocks: z
      .array(
        z.object({ start: TimeString.nullish(), end: TimeString.nullish() }),
      )
      .nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/widgetHours/wednesday", {
    enabled: z.boolean().nullish(),
    timeBlocks: z
      .array(
        z.object({ start: TimeString.nullish(), end: TimeString.nullish() }),
      )
      .nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/widgetHours/thursday", {
    enabled: z.boolean().nullish(),
    timeBlocks: z
      .array(
        z.object({ start: TimeString.nullish(), end: TimeString.nullish() }),
      )
      .nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/widgetHours/friday", {
    enabled: z.boolean().nullish(),
    timeBlocks: z
      .array(
        z.object({ start: TimeString.nullish(), end: TimeString.nullish() }),
      )
      .nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/widgetHours/saturday", {
    enabled: z.boolean().nullish(),
    timeBlocks: z
      .array(
        z.object({ start: TimeString.nullish(), end: TimeString.nullish() }),
      )
      .nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/widgetHours/sunday", {
    enabled: z.boolean().nullish(),
    timeBlocks: z
      .array(
        z.object({ start: TimeString.nullish(), end: TimeString.nullish() }),
      )
      .nullish(),
  }).shape,
  "customerWidget/general/widgetQuickActions": z
    .array(QuickActionZod)
    .nullish(),
  "customerWidget/general/widgetQuickLinks": z
    .array(z.object({ title: z.string(), url: z.url() }))
    .nullish(),

  ...createPrefixedSchema("customerWidget/support", {
    chatExpirationMessage: z.string().nullish(),
    chatExpirationMinutes: z.number().int().min(1).nullish(),
    emailConversionPrepMessage: z.string().nullish(),
    liveChatConversionPrepMessage: z.string().nullish(),
    hideSupportWidgetFeatures: z.boolean().nullish(),
    messagingHeadingText: z.string().nullish(),
    unresponsiveConversionEnabled: z.boolean().nullish(),
    unresponsiveConversionMinutes: z.number().int().min(0).nullish(),
    unresponsiveConversionSeconds: z.number().int().min(0).max(59).nullish(),
  }).shape,
  ...createPrefixedSchema(
    "customerWidget/support/unresponsiveConversionEmail",
    {
      email: z.string().nullish(),
      integrationKind: z.string().nullish(),
      name: z.string().nullish(),
    },
  ).shape,

  "customerWidget/customerAccounts/accountTabSections": z
    .array(
      z.object({
        type: AccountTabSectionType,
        header: z.string(),
        enabled: z.boolean(),
      }),
    )
    .nullish(),
  "customerWidget/customerAccounts/registerHeader": z.string().nullish(),
  "customerWidget/customerAccounts/registerSubtext": z.string().nullish(),
  "customerWidget/customerAccounts/showAccountsPage": z.boolean().nullish(),
});

const AiAutopilotSettingsSchema_ = createPrefixedSchema("ai/autopilot", {
  enabled: z.boolean().nullish(),
  maxDailyTickets: z.number().int().min(0).nullish(),
  confidenceThreshold: z.number().min(0).max(1).nullish(),
  escalationKeywords: z.array(z.string()).nullish(),
  autoCloseEnabled: z.boolean().nullish(),
  autoCloseDelayHours: z.number().int().min(1).nullish(),
  approvalRequired: z.boolean().nullish(),
  approverEmails: z.array(z.email()).nullish(),
  enabledChannels: z.array(z.string()).nullish(),
});

const KnowledgeBaseSettingsSchema_ = createPrefixedSchema("knowledgeBase", {
  enabled: z.boolean().nullish(),
  autoUpdate: z.boolean().nullish(),
  sources: z
    .array(
      z.object({
        type: createEnum(["url", "file", "text"]),
        content: z.string(),
        title: z.string().nullish(),
        isActive: z.boolean(),
      }),
    )
    .nullish(),
  lastUpdated: ISODateString.nullish(),
  indexingEnabled: z.boolean().nullish(),
});

export enum OrderEditingDisallowEvent {
  LABEL_CREATED = "label_created",
  PACKING_SLIP_PRINTED = "packing_slip_printed",
  PICK_SESSION_CREATED = "pick_session_created",
}

const OrderEditingDisallowEventZod = createEnum([
  OrderEditingDisallowEvent.LABEL_CREATED,
  OrderEditingDisallowEvent.PACKING_SLIP_PRINTED,
  OrderEditingDisallowEvent.PICK_SESSION_CREATED,
]);

const OrderEditingSettingsSchema_ = z.object({
  "orderEditing/useOmsEvents": z.boolean().nullish(),
  ...createPrefixedSchema("orderEditing/tools/cancelOrder", {
    enabled: z.boolean().nullish(),
    cancelOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/changeAddress", {
    enabled: z.boolean().nullish(),
    changeOrderAddressTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/addOrderDiscount", {
    enabled: z.boolean().nullish(),
    addOrderDiscountTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/addOrderGiftNote", {
    enabled: z.boolean().nullish(),
    addOrderGiftNoteTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/updateOrder", {
    enabled: z.boolean().nullish(),
    updateOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
    addItem: z.boolean().nullish(),
    removeItem: z.boolean().nullish(),
    changeVariant: z.boolean().nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
});

export const FlatTeamSettingsSchema = z.object({
  ...SupportSettingsSchema_.shape,
  ...ConciergeSettingsSchema_.shape,
  ...ConciergeTicketHandoffSettingsSchema_.shape,
  ...CustomerWidgetSettingsSchema_.shape,
  ...AiAutopilotSettingsSchema_.shape,
  ...KnowledgeBaseSettingsSchema_.shape,
  ...OrderEditingSettingsSchema_.shape,
});

export type FlatTeamSettings = z.infer<typeof FlatTeamSettingsSchema>;

export const FlatTeamSettingKeys = Object.keys(
  FlatTeamSettingsSchema.shape,
) as Array<keyof FlatTeamSettings>;

export const ValidFlatSettingKeysSchema = z.array(
  createEnum(
    FlatTeamSettingKeys as [
      keyof FlatTeamSettings,
      ...Array<keyof FlatTeamSettings>,
    ],
  ),
);

/**
 * Utility function to create a schema for a subset of keys
 */
export function createPickedFlatSettingsSchema<
  K extends keyof FlatTeamSettings,
>(keys: readonly K[]): z.ZodType<Pick<FlatTeamSettings, K>> {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const key of keys) {
    const keySchema = FlatTeamSettingsSchema.shape[key];
    if (keySchema) {
      schemaShape[key] = keySchema;
    } else {
      throw new Error(`Unknown setting key: ${key}`);
    }
  }

  return z.object(schemaShape) as z.ZodType<Pick<FlatTeamSettings, K>>;
}

const UpdateFlatTeamSettingsSchema = zExt.optionalToNullable(
  FlatTeamSettingsSchema,
);
export type UpdateFlatTeamSettings = z.infer<
  typeof UpdateFlatTeamSettingsSchema
>;

export function createPickedUpdateFlatSettingsSchema<
  K extends keyof FlatTeamSettings,
>(keys: readonly K[]): z.ZodType<Pick<UpdateFlatTeamSettings, K>> {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const key of keys) {
    const keySchema = UpdateFlatTeamSettingsSchema.shape[key];
    if (keySchema) {
      schemaShape[key] = keySchema;
    } else {
      throw new Error(`Unknown setting key: ${key}`);
    }
  }

  return z.object(schemaShape) as z.ZodType<Pick<UpdateFlatTeamSettings, K>>;
}

// Type guard for checking if a key exists in the flat settings schema
export function isFlatSettingKey(key: string): key is keyof FlatTeamSettings {
  return key in FlatTeamSettingsSchema.shape;
}

/**
 * Utility to get the schema for a specific key
 */
export function getSchemaForKey(
  key: keyof FlatTeamSettings,
): z.ZodTypeAny | undefined {
  return FlatTeamSettingsSchema.shape[key];
}

export const FlatTeamSettingsPartialRecordSchema = z.partialRecord(
  z.keyof(FlatTeamSettingsSchema),
  z.literal(1),
);
export type FlatTeamSettingsPartialRecord = z.infer<
  typeof FlatTeamSettingsPartialRecordSchema
>;
